/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import deferToClientSideRender from 'js/lib/deferToClientSideRender';

import { Typography2, breakpoints } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import { TrackedCdsButton } from 'bundles/common/components/withSingleTracked';
import localStorageEx from 'bundles/common/utils/localStorageEx';
import CourseraPlusNotificationBanner from 'bundles/coursera-plus-common/components/lihp/CourseraPlusNotificationBanner';
import {
  generateImpressionForPlusMonthlyToAnnualUpgradeOnLIHP,
  isPlusMonthlyToAnnualUpgradeEnabledOnLIHP,
  useIsEligibleForPlusMonthlyToAnnualUpgrade,
  usePlusMonthlyToAnnualUpgradeData,
} from 'bundles/coursera-plus-common/utils/plusMonthlyToAnnualUpgradeUtils';
import { getEligibleCourseraPlusMonthlySubscription } from 'bundles/coursera-plus-common/utils/subscriptionUtils';
import { useNotificationBannersHandler } from 'bundles/coursera-plus-common/utils/useNotificationBannersHandler';
import AnnualUpgradeConfirmationModal from 'bundles/my-purchases-graphql/components/upgrade/AnnualUpgradeConfirmationModal';
import AnnualUpgradeModal from 'bundles/my-purchases-graphql/components/upgrade/AnnualUpgradeModal';
import StartAnnualUpgradeModal from 'bundles/my-purchases-graphql/components/upgrade/StartAnnualUpgradeModal';
import { useGetLoggedInUserSubscriptions } from 'bundles/my-purchases-graphql/components/utils/useGetLoggedInUserSubscriptions';
import type SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';

import _t from 'i18n!nls/coursera-plus';

const styles = {
  upgradeSubscriptionWrapper: css`
    > div {
      display: inline-block;
    }
  `,
  upgradeSubscriptionLink: css`
    margin: 0 4px 0 10px;
    padding: 0;
    vertical-align: bottom;
    text-decoration: underline;

    ${breakpoints.down('xs')} {
      margin: 0;
    }

    .cds-button-endIcon {
      margin-left: 4px;
    }
  `,
};

const CourseraPlusMonthlyToAnnualUpgradeNotification = () => {
  const [hideNotification, setHideNotification] = useState(false);
  const [showStartAnnualUpgradeModal, setShowStartAnnualUpgradeModal] = useState(false);
  const [showAnnualUpgradeModal, setShowAnnualUpgradeModal] = useState(false);
  const [hasUpgraded, setHasUpgraded] = useState(false);
  const { subscriptions } = useGetLoggedInUserSubscriptions({ skip: false });
  const subscription = getEligibleCourseraPlusMonthlySubscription(subscriptions || []);
  const { monthlySavings, courseraPlusAnnualPrice } = usePlusMonthlyToAnnualUpgradeData(
    (subscription || {}) as SubscriptionsV1,
    'LIHP'
  );

  const { isEligibleForPlusMonthlyToAnnualUpgrade, loading: loadingUpgradeEligibility } =
    useIsEligibleForPlusMonthlyToAnnualUpgrade(subscriptions);

  const { cplusAnnualUpgradeNotificationBannerData, loading: loadingBannerData } = useNotificationBannersHandler();

  useEffect(() => {
    if (loadingUpgradeEligibility || loadingBannerData) {
      return;
    }

    generateImpressionForPlusMonthlyToAnnualUpgradeOnLIHP(
      isEligibleForPlusMonthlyToAnnualUpgrade && cplusAnnualUpgradeNotificationBannerData.showBanner
    );
  }, [
    cplusAnnualUpgradeNotificationBannerData.showBanner,
    isEligibleForPlusMonthlyToAnnualUpgrade,
    loadingUpgradeEligibility,
    loadingBannerData,
  ]);

  const storageKey = cplusAnnualUpgradeNotificationBannerData.storageKey;

  const content = (
    <>
      <Typography2 component="h2" variant="subtitleMedium">
        <FormattedMessage
          message={_t('Save {monthlySavings} a month by switching to Coursera Plus annual')}
          monthlySavings={monthlySavings}
          tagName="div"
        />
      </Typography2>
      <Typography2 component="p" variant="bodySecondary" css={styles.upgradeSubscriptionWrapper}>
        <TrackedCdsButton
          css={styles.upgradeSubscriptionLink}
          size="small"
          variant="ghost"
          edgeAlign="start"
          icon={<ArrowNextIcon size="small" />}
          onClick={() => setShowStartAnnualUpgradeModal(true)}
          trackingName="lihp_cplus_monthly_to_annual_cta"
        >
          {_t('Switch your subscription')}
        </TrackedCdsButton>
      </Typography2>
    </>
  );

  const hideUpgradeNotification =
    !cplusAnnualUpgradeNotificationBannerData.showBanner ||
    !isEligibleForPlusMonthlyToAnnualUpgrade ||
    !isPlusMonthlyToAnnualUpgradeEnabledOnLIHP() ||
    !subscription ||
    !courseraPlusAnnualPrice ||
    !monthlySavings;
  const hasDismissedNotification = localStorageEx.getItem(storageKey, JSON.parse, false);

  if (hideUpgradeNotification || hasDismissedNotification) {
    return null;
  }

  return (
    <>
      <StartAnnualUpgradeModal
        isOpen={showStartAnnualUpgradeModal}
        onClose={() => {
          setShowStartAnnualUpgradeModal(false);
        }}
        openAnnualUpgradeModal={() => {
          setShowStartAnnualUpgradeModal(false);
          setShowAnnualUpgradeModal(true);
        }}
        monthlySavings={monthlySavings}
        subscription={subscription}
        courseraPlusAnnualPrice={courseraPlusAnnualPrice}
      />
      <AnnualUpgradeModal
        subscription={subscription}
        courseraPlusAnnualPrice={courseraPlusAnnualPrice}
        onClose={() => {
          setShowAnnualUpgradeModal(false);
        }}
        onUpgradeSuccess={() => {
          setShowAnnualUpgradeModal(false);
          setHasUpgraded(true);
          setHideNotification(true);
        }}
        isOpen={showAnnualUpgradeModal}
      />
      {hasUpgraded && <AnnualUpgradeConfirmationModal />}
      <CourseraPlusNotificationBanner content={content} storageKey={storageKey} hideNotification={hideNotification} />
    </>
  );
};
export default deferToClientSideRender(CourseraPlusMonthlyToAnnualUpgradeNotification);
